// src/pages/Enseignants/ListingAdherents/ModalFicheAdherent.jsx

import React, { useMemo, useState } from "react";
import {
  FaTimes,
  FaEdit,
  FaPlus,
  FaCalendar,
} from "react-icons/fa";

// Reuse admin components
import AdherentDetails from "../../PagesAdministrateurs/DetailsAdherent/AdherentDetails";
import AdherentGroupCategory from "../../PagesAdministrateurs/DetailsAdherent/AdherentGroupCategory";
import AdherentManagers from "../../PagesAdministrateurs/DetailsAdherent/AdherentManagers";
import AdherentPassageCeinture from "../../PagesAdministrateurs/DetailsAdherent/AdherentPassageCeinture";
import AdherentPalmares from "../../PagesAdministrateurs/DetailsAdherent/AdherentPalmares";
import AdherentCompetitions from "../../PagesAdministrateurs/DetailsAdherent/AdherentCompetitions";
import DossierInscription from "../../PagesAdministrateurs/DetailsAdherent/DossierInscription";
import ReglementInscription from "../../PagesAdministrateurs/DetailsAdherent/ReglementInscription";
import AchatsAdherent from "../../PagesAdministrateurs/DetailsAdherent/AchatsAdherent";
import AttendanceList from "../../PagesAdministrateurs/DetailsAdherent/AttendanceList";

import EditAdherentAdminModal from "./../../PagesAdministrateurs/ListingAdherentsAdmin/EditAdherentAdminModal";

const defaultAvatar = (genre) =>
  genre === "Féminin" ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";

const getStatusPillClass = (statut) => {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-800 ring-1 ring-green-300/70";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-800 ring-1 ring-green-200/70";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900 ring-1 ring-yellow-300/70";
    case "Cours d'essai":
      return "bg-red-300 text-slate-900 ring-1 ring-red-300/70";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800 ring-1 ring-slate-300/70";
    case "Ancien Licencié":
      return "bg-slate-900 text-white ring-1 ring-slate-900/50";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white ring-1 ring-slate-500/70";
    default:
      return "bg-slate-200 text-slate-800 ring-1 ring-slate-200/70";
  }
};

const TABS = [
  { key: "profil", label: "Profil" },
  { key: "groupes", label: "Groupes" },
  { key: "managers", label: "Managers" },
  { key: "ceintures", label: "Ceintures" },
  { key: "competitions", label: "Compétitions" },
  { key: "palmares", label: "Palmarès" },
  { key: "dossier", label: "Dossier" },
  { key: "reglement", label: "Règlement" },
  { key: "achats", label: "Achats" },
  { key: "presences", label: "Présences" },
];

const ModalFicheAdherent = ({
  adherent,
  onClose,
  isAdmin,
  onEdit,
  onManageGroups,
}) => {
  const [tab, setTab] = useState("profil");
  const [isEditOpen, setIsEditOpen] = useState(false);

  const id = adherent?.id;

  const headerName = useMemo(
    () =>
      `${(adherent?.nom || "").toUpperCase()} ${
        adherent?.prenom || ""
      }`.trim(),
    [adherent]
  );

  const toAbsolute = (u) => {
    if (!u) return "";

    const s = String(u).trim();

    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    ) {
      return s;
    }

    if (!s.startsWith("/uploads")) return s;

    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  if (!adherent) return null;

  const openExternalAfterClose = (callback) => {
    onClose?.();

    window.setTimeout(() => {
      callback?.(adherent);
    }, 0);
  };

  const handleEdit = () => {
    if (onEdit) {
      openExternalAfterClose(onEdit);
      return;
    }

    setIsEditOpen(true);
  };

  const handleManageGroups = () => {
    if (!onManageGroups) return;
    openExternalAfterClose(onManageGroups);
  };

  if (isAdmin && isEditOpen && !onEdit) {
    return (
      <EditAdherentAdminModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        refreshList={null}
        adherent={adherent}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[999]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div
        className="relative mx-auto my-2 h-[calc(100vh-1rem)] w-[100vw] overflow-hidden rounded-none bg-white shadow-2xl ring-1 ring-slate-200/70 dark:bg-slate-900 dark:ring-slate-700/70 sm:my-6 sm:h-[85vh] sm:w-[92vw] sm:max-w-[1100px] sm:rounded-2xl"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
          <div className="flex items-center gap-3 px-4 py-3 sm:px-6 sm:py-4">
            <img
              src={
                adherent?.photo_url
                  ? toAbsolute(adherent.photo_url)
                  : defaultAvatar(adherent?.genre || adherent?.sexe)
              }
              alt={headerName}
              className="h-12 w-12 rounded-full object-cover ring-2 ring-white/70 sm:h-14 sm:w-14"
            />

            <div className="min-w-0">
              <div className="truncate text-base font-semibold sm:text-lg">
                {headerName}
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 ${getStatusPillClass(
                    adherent?.statut_inscription
                  )}`}
                >
                  {adherent?.statut_inscription || "—"}
                </span>

                {adherent?.saison_active && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-200 px-2 py-0.5 text-emerald-900 ring-1 ring-emerald-300">
                    <FaCalendar /> {adherent.saison_active}
                  </span>
                )}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2">
              {isAdmin && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost gap-2 text-white hover:bg-white/15"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleEdit();
                    }}
                    title="Éditer l’adhérent"
                  >
                    <FaEdit />
                    <span className="hidden sm:inline">Éditer</span>
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-ghost gap-2 text-white hover:bg-white/15"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleManageGroups();
                    }}
                    title="Gérer les groupes"
                    disabled={!onManageGroups}
                  >
                    <FaPlus />
                    <span className="hidden sm:inline">Groupes</span>
                  </button>
                </>
              )}

              <button
                type="button"
                onClick={onClose}
                className="btn btn-sm btn-ghost text-white hover:bg-white/15"
                aria-label="Fermer"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="px-2 pb-2 sm:px-4">
            <div className="no-scrollbar overflow-x-auto">
              <div className="inline-flex gap-2">
                {TABS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    className={`rounded-full px-3 py-1.5 text-sm transition ${
                      tab === key
                        ? "bg-white text-slate-900"
                        : "bg-white/10 text-white hover:bg-white/20"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="h-[calc(100%-128px)] overflow-y-auto px-4 py-4 sm:h-[calc(85vh-120px)] sm:px-6">
          {tab === "profil" && (
            <AdherentDetails
              adherent={adherent}
              showTeacherNotes={false}
              onEdit={() => handleEdit()}
            />
          )}

          {tab === "groupes" && <AdherentGroupCategory adherentId={id} />}
          {tab === "managers" && <AdherentManagers adherentId={id} />}
          {tab === "ceintures" && <AdherentPassageCeinture adherentId={id} />}
          {tab === "competitions" && <AdherentCompetitions adherentId={id} />}
          {tab === "palmares" && <AdherentPalmares adherentId={id} />}
          {tab === "dossier" && <DossierInscription adherentId={id} />}

          {tab === "reglement" && (
            <ReglementInscription
              adherentId={id}
              saison={adherent?.saison_active}
            />
          )}

          {tab === "achats" && <AchatsAdherent adherentId={id} />}
          {tab === "presences" && <AttendanceList adherentId={id} />}
        </div>
      </div>
    </div>
  );
};

export default ModalFicheAdherent;